/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

/* eslint-disable @typescript-eslint/ban-ts-comment, max-params */
// @ts-nocheck

/**
 *
 * Example account login:
 * https://XXXXXXXXXXX1.signin.aws.amazon.com/console/ec2?region=eu-west-1#Instances:sort=instanceId
 * https://XXXXXXXXXXX1.signin.aws.amazon.com/console/ec2?region=eu-west-1#LoadBalancers:sort=loadBalancerName
 * https://XXXXXXXXXXX1.signin.aws.amazon.com/console/lambda?region=eu-west-1#/functions/twobytwo-lambda-SentimentAnalysis-XN8MLZP7IX36?tab=graph
 *
 * Example once logged in:
 * https://eu-west-1.console.aws.amazon.com/iam/home?region=eu-west-1#/users
 * https://eu-west-1.console.aws.amazon.com/ec2/v2/home?region=eu-west-1#Instances:sort=instanceId - ec2
 * https://eu-west-1.console.aws.amazon.com/ec2/v2/home?region=eu-west-1#LoadBalancers:sort=loadBalancerName - load Balancer
 * https://eu-west-1.console.aws.amazon.com/ec2/v2/home?region=eu-west-1#SecurityGroups:sort=groupId - security Group
 * https://eu-west-1.console.aws.amazon.com/lambda/home?region=eu-west-1#/functions/twobytwo-lambda-ClearDown-4EF6H592XV4?tab=graph - lambda function
 * https://eu-west-1.console.aws.amazon.com/vpc/home?region=eu-west-1#vpcs:sort=VpcId - VPC
 * https://eu-west-1.console.aws.amazon.com/vpc/home?region=eu-west-1#Addresses:sort=PublicIp - elastic ip
 * https://eu-west-1.console.aws.amazon.com/vpc/home?region=eu-west-1#RouteTables:sort=routeTableId - route table
 * https://eu-west-1.console.aws.amazon.com/vpc/home?region=eu-west-1#subnets:sort=SubnetId - subnet
 * https://eu-west-1.console.aws.amazon.com/ec2/v2/home?region=eu-west-1#Volumes:sort=desc:createTime - volumes
 * https://eu-west-1.console.aws.amazon.com/cloudwatch/home?region=eu-west-1#alarm:alarmFilter=ANY
 *
 *
 * Example ARN:
 * arn:aws:ec2:eu-west-1:XXXXXXXXXXX1:instance/i-0de5d3dffcb58d553
 * arn:aws:ec2:eu-west-1:XXXXXXXXXXX1:subnet/subnet-07735f25386872c88
 * arn:aws:ec2:eu-west-1:XXXXXXXXXXX1:volume/vol-000b6f4ad820cd78a
 * arn:aws:lambda:eu-west-1:XXXXXXXXXXX1:function:twobytwo-lambda-ClearDown-4EF6H592XV4
 * arn:aws:dynamodb:eu-west-1:XXXXXXXXXXX1:table/autosave
 * arn:aws:autoscaling:eu-west-1:XXXXXXXXXXX1:autoScalingGroup:ef194843-edb2-4178-9e0f-72cb6372f6f8:autoScalingGroupName/zoom-ec2-autoscaling-group
 *
 */

/*
    "Amazon-RDS" : "AWS::RDS::DBInstance",
    "DBSubnetGroup" : "AWS::RDS::DBSubnetGroup",
    "Amazon-DynamoDB" : "AWS::DynamoDB::Table",
    "AWS-CloudFormation" : "AWS::CloudFormation::Stack",
    "Amazon-CloudWatch_Alarm_light-bg":"AWS::CloudWatch::Alarm",
*/

import { urlEndings, resourceTypeMap, providerTooltipMap } from "./constants";
import { createMultiMethod } from "./functionUtil";

const dispatchFunction = (node) => {
  return node?.callType;
};

const noMatch = () => {
  return { undefined };
};

const buildConsoleURL = createMultiMethod(dispatchFunction, noMatch);

buildConsoleURL.s3 = (urlBuilder) => {
  const loginURL = buildLoginURLS3(
    urlBuilder.node.properties.accountId,
    urlBuilder.node.properties.awsRegion,
    urlBuilder.node.properties.resourceName
  );
  const loggedInURL = buildLoggedInURLS3(
    urlBuilder.node.properties.accountId,
    urlBuilder.node.properties.awsRegion,
    urlBuilder.node.properties.resourceName
  );
  return { loginURL, loggedInURL };
};

buildConsoleURL.default = (urlBuilder) => {
  const loginURL = buildLoginURL(
    urlBuilder.node.properties.accountId,
    urlBuilder.node.properties.awsRegion,
    urlBuilder.urlType,
    urlBuilder.urlEnding
  );
  const loggedInURL = buildLoggedInURL(
    urlBuilder.node.properties.awsRegion,
    urlBuilder.urlType,
    urlBuilder.urlEnding
  );
  return { loginURL, loggedInURL };
};

buildConsoleURL.iam = (urlBuilder) => {
  const loginURL = buildLoginURLIAM(
    urlBuilder.node.properties.accountId,
    urlBuilder.node.properties.awsRegion,
    urlBuilder.urlType,
    urlBuilder.urlEnding
  );
  const loggedInURL = buildLoggedInURLIAM(urlBuilder.urlEnding);
  return { loginURL, loggedInURL };
};

buildConsoleURL.lambda = (urlBuilder) => {
  const loginURL = buildLogInURLLambda(
    urlBuilder.node.properties.accountId,
    urlBuilder.node.properties.awsRegion,
    urlBuilder.node.properties.resourceName
  );
  const loggedInURL = buildLoggedInURLLambda(
    urlBuilder.node.properties.awsRegion,
    urlBuilder.node.properties.resourceName
  );
  return { loginURL, loggedInURL };
};

buildConsoleURL.apigateway = (urlBuilder) => {
  const resource =
    urlBuilder.node.properties.restApiId ===
    urlBuilder.node.properties.resourceId
      ? undefined
      : urlBuilder.node.properties.resourceId;
  const loginURL = buildLogInURLAPIGateway(
    urlBuilder.node.properties.accountId,
    urlBuilder.node.properties.awsRegion,
    urlBuilder.node.properties.restApiId,
    resource
  ); // Method = "");
  const loggedInURL = buildLoggedInURLAPIGateway(
    urlBuilder.node.properties.awsRegion,
    urlBuilder.node.properties.restApiId,
    resource
  );
  return { loginURL, loggedInURL };
};

buildConsoleURL.apigatewayMethod = (urlBuilder) => {
  const split = urlBuilder.node.properties.resourceId.split("_");
  const restApiId = split[0];
  const resourceId = split[1];
  const method = split[2];

  const loginURL = buildLogInURLAPIGateway(
    urlBuilder.node.properties.accountId,
    urlBuilder.node.properties.awsRegion,
    restApiId,
    resourceId,
    method
  );
  const loggedInURL = buildLoggedInURLAPIGateway(
    urlBuilder.node.properties.awsRegion,
    restApiId,
    resourceId,
    method
  );
  return { loginURL, loggedInURL };
};

/**
 * XTODO find url to support login
 *  let loginURL = buildLoginAutoscale(
     urlBuilder.node.properties.accountId,
     urlBuilder.node.properties.awsRegion,
     urlBuilder.node.properties.resourceName
   );
 */
buildConsoleURL.autoscaling = (urlBuilder) => {
  const loggedInURL = buildLoggedInURLAutoscale(
    urlBuilder.node.properties.awsRegion,
    urlBuilder.node.properties.resourceName
  );
  const loginURL = buildLoginAutoscale(
    urlBuilder.node.properties.accountId,
    urlBuilder.node.properties.awsRegion,
    urlBuilder.node.properties.resourceName
  );
  return { loginURL, loggedInURL };
};

const buildLoggedInURLAPIGateway = (region, restApiId, resource, method) => {
  let url = `https://${region}.console.aws.amazon.com/apigateway/home?region=${region}#/apis/${restApiId}/resources`;
  url = appendItem(url, resource);
  url = appendItem(url, method, "methods");
  return url;
};

const buildLogInURLAPIGateway = (
  accountId,
  region,
  restApiId,
  resource,
  method
) => {
  let url = `https://${accountId}.signin.aws.amazon.com/console/apigateway?region=${region}#/apis/${restApiId}/resources`;
  url = appendItem(url, resource);
  url = appendItem(url, method, "methods");
  return url;
};

const appendItem = (url, item, option) => {
  const itemUrl = option ? url + "/" + option + "/" + item : url + "/" + item;

  return item ? itemUrl : url;
};

const buildLoggedInURLAutoscale = (region, id) => {
  return `https://${region}.console.aws.amazon.com/ec2/autoscaling/home?region=${region}#AutoScalingGroups:id=${id};view=details`;
};

const buildLoginAutoscale = (accountId, region, id) => {
  return `https://${accountId}.signin.aws.amazon.com/console/ec2/autoscaling/home?region=${region}#AutoScalingGroups:id=${id};view=details`;
};

const buildLoggedInURLLambda = (region, f) => {
  return `https://${region}.console.aws.amazon.com/lambda/home?region=${region}#/functions/${f}?tab=graph`;
};

const buildLogInURLLambda = (accountId, region, f) => {
  return `https://${accountId}.signin.aws.amazon.com/console/lambda?region=${region}#/functions/${f}?tab=graph`;
};

const buildLoggedInURLS3 = (accountId, region, bucketName) => {
  return `https://s3.console.aws.amazon.com/s3/buckets/${bucketName}/?region=${region}`;
};

const buildLoginURLS3 = (accountId, region, bucketName) => {
  return `https://${accountId}.signin.aws.amazon.com/console/s3?bucket=${bucketName}`;
};

const buildLoginURL = (accountId, region, type, component) => {
  return `https://${accountId}.signin.aws.amazon.com/console/${type}?region=${region}#${component}`;
};

const buildLoggedInURL = (region, type, component) => {
  return `https://${region}.console.aws.amazon.com/${type}/v2/home?region=${region}#${component}`;
};

const buildLoginURLIAM = (accountId, region, type, component) => {
  return `https://${accountId}.signin.aws.amazon.com/console/iam?home?#${component}`;
};

const buildLoggedInURLIAM = (component) => {
  return `https://console.aws.amazon.com/iam/home?#${component}`;
};

const azureLoggedInURL = (resourceId) => {
  return `https://portal.azure.com/#resource${resourceId}`;
};

const gcpLoggedInURL = (organizationId, gcpId, resourceType) => {
  if (resourceType === "project") {
    return `https://console.cloud.google.com/home/dashboard?organizationId=${organizationId}&project=${gcpId}`;
  }
  return `https://console.cloud.google.com/search;q=${encodeURIComponent(
    gcpId
  )}`;
};

type Node = {
  properties: {
    resourceId: string;
    resourceType: string;
    accountId: string;
    resourceName: string;
    restApiId: string;
    awsRegion: string;
  };
};

type CalcProperties = {
  url: string;
  type: string;
  callType: string;
};

type Calculation = {
  node: Node;
  urlType: string;
  urlEnding: string;
  callType?: string;
};

export type InputResource = {
  cloudAccountId: string;
  region: string;
  name: string;
  id: string;
  type?: string;
  provider: string;
  providerId?: string;
  providerType?: string;
};

export const mapResource = (resource: InputResource): Node => {
  return {
    properties: {
      accountId: resource.cloudAccountId,
      awsRegion: resource.region,
      resourceName: resource.name,
      resourceId: resource.id,
      resourceType: resource.type || resource.providerType,
    },
  };
};

export type ConsoleUrls = {
  loggedInURL: string;
  loginURL: string;
  undefined: undefined;
};

function isValidProps(props: Node["properties"]): boolean {
  return Object.values(props).every((value) => typeof value === "string");
}

export const getConsoleURLs = (node: Node): ConsoleUrls => {
  if (isValidProps(node.properties)) {
    const mappedResourceType = resourceTypeMap[node.properties.resourceType];
    const calcProperties: CalcProperties = urlEndings[mappedResourceType];

    const calculation: Calculation =
      calcProperties == null
        ? { node, urlType: "undefined", urlEnding: "" }
        : {
            node,
            urlType: calcProperties.type,
            urlEnding: calcProperties.url,
            callType: calcProperties.callType,
          };

    return buildConsoleURL(calculation);
  }

  return { undefined };
};

export const getAzurePortalURLs = (input: InputResource): string => {
  return azureLoggedInURL(input.providerId);
};

export const getGCPConsoleURLs = (input: InputResource): string => {
  return gcpLoggedInURL(input.cloudAccountId, input.providerId, input.type);
};

export const getProviderTooltip = (provider?: string): string => {
  if (provider) {
    return providerTooltipMap[provider] ?? providerTooltipMap.AWS;
  }
  return providerTooltipMap.AWS;
};
